import { useEffect, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import { EASE } from "./Reveal";

const MIN_MS = 1100;
const MAX_MS = 4000;

const useImageReady = (src) => {
    const [ready, setReady] = useState(false);
    useEffect(() => {
        const start = Date.now();
        let done = false;
        const finish = () => {
            if (done) return;
            done = true;
            setTimeout(() => setReady(true), Math.max(0, MIN_MS - (Date.now() - start)));
        };
        const img = new Image();
        img.decoding = "async";
        img.onload = () => (img.decode ? img.decode().then(finish, finish) : finish());
        img.onerror = finish;
        img.src = src;
        const guard = setTimeout(finish, MAX_MS);
        return () => clearTimeout(guard);
    }, [src]);
    return ready;
};

const Word = ({ children, delay }) => (
    <span className="mask-line">
        <motion.span className="block" initial={{ y: "112%" }} animate={{ y: "0%" }} transition={{ duration: 0.9, ease: EASE, delay }}>
            {children}
        </motion.span>
    </span>
);

const Fade = ({ children, delay, className }) => (
    <motion.span initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 0.6, delay }} className={className}>
        {children}
    </motion.span>
);

const Screen = () => (
    <motion.div
        key="preloader"
        data-testid="preloader"
        className="fixed inset-0 z-[100] bg-[var(--ink)] text-[var(--bone)] flex flex-col justify-between px-6 md:px-12 pt-24 pb-10 md:pb-14 will-change-transform"
        exit={{ y: "-100%" }}
        transition={{ duration: 1.1, ease: EASE }}
    >
        <Fade delay={0.3} className="font-mono2 text-[10px] md:text-xs tracking-[0.3em] text-[var(--mute)] self-end">
            PORTFOLIO — 2026
        </Fade>
        <div className="flex items-end justify-between gap-6">
            <p className="font-display uppercase leading-[0.95] tracking-tight text-4xl sm:text-5xl lg:text-6xl" data-testid="preloader-mark">
                <Word delay={0.1}>Marco</Word>
                <Word delay={0.22}>Giavazzi</Word>
            </p>
            <Fade delay={0.5} className="font-script text-[var(--mute)] text-xl md:text-3xl -rotate-2 pb-1">
                archive
            </Fade>
        </div>
        <motion.div
            aria-hidden="true"
            className="absolute left-0 bottom-0 w-full h-px bg-[var(--red)] origin-left"
            initial={{ scaleX: 0 }}
            animate={{ scaleX: 1 }}
            transition={{ duration: MIN_MS / 1000, ease: "linear" }}
        />
    </motion.div>
);

export const Preloader = ({ src, onReveal }) => {
    const ready = useImageReady(src);
    useEffect(() => {
        if (ready) onReveal();
    }, [ready, onReveal]);
    return <AnimatePresence>{!ready && <Screen />}</AnimatePresence>;
};
